import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, X, Check, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import TagBadge from './TagBadge';
import { productTagsApi } from '../../services/api';
import { toast } from 'sonner';

// Predefined color palette
const TAG_COLORS = [
  { value: '#ef4444', name: 'Red' },
  { value: '#f97316', name: 'Orange' },
  { value: '#eab308', name: 'Yellow' },
  { value: '#22c55e', name: 'Green' },
  { value: '#14b8a6', name: 'Teal' },
  { value: '#06b6d4', name: 'Cyan' },
  { value: '#3b82f6', name: 'Blue' },
  { value: '#6366f1', name: 'Indigo' },
  { value: '#8b5cf6', name: 'Violet' },
  { value: '#a855f7', name: 'Purple' },
  { value: '#ec4899', name: 'Pink' },
  { value: '#64748b', name: 'Slate' },
];

/**
 * TagInput - Autocomplete input for selecting/creating tags
 * 
 * @param {string} productId - Product ID for tag scope
 * @param {array} selectedTags - Array of selected tag objects
 * @param {function} onTagsChange - Callback with updated tags array
 * @param {boolean} disabled - Disable input
 * @param {string} placeholder - Input placeholder
 */
const TagInput = ({
  productId,
  selectedTags = [],
  onTagsChange,
  disabled = false,
  placeholder = "Add tags..."
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [newTagColor, setNewTagColor] = useState(null);
  
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fetch suggestions as user types
  const fetchSuggestions = useCallback(async (query) => {
    if (!productId) return;
    
    setIsLoading(true);
    try {
      const res = await productTagsApi.search(productId, query);
      const filtered = (res.data.tags || []).filter(
        tag => !selectedTags.some(t => t.tag_id === tag.tag_id)
      );
      setSuggestions(filtered);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    } finally {
      setIsLoading(false);
    }
  }, [productId, selectedTags]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    if (inputValue.trim()) {
      debounceTimer.current = setTimeout(() => {
        fetchSuggestions(inputValue);
      }, 200);
    } else {
      setSuggestions([]);
    }
    
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [inputValue, fetchSuggestions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
        setShowColorPicker(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Select an existing tag
  const selectTag = (tag) => {
    onTagsChange([...selectedTags, tag]);
    setInputValue('');
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  // Create a new tag
  const createTag = async (name, color) => {
    if (!productId || !name.trim()) return;
    
    setIsLoading(true);
    try {
      const res = await productTagsApi.create(productId, { 
        name: name.trim(),
        color: color || null // Let backend auto-assign if null
      });
      selectTag(res.data);
      toast.success(`Created tag "${name}"`);
    } catch (err) {
      if (err.response?.status === 400) {
        // Tag already exists, try to find and select it
        const existingTagId = err.response.headers['x-existing-tag-id'];
        if (existingTagId) {
          const existing = suggestions.find(t => t.tag_id === existingTagId);
          if (existing) {
            selectTag(existing);
            return;
          }
        }
        toast.error('Tag with this name already exists');
      } else {
        toast.error('Failed to create tag');
      }
    } finally {
      setIsLoading(false);
      setShowColorPicker(false);
      setNewTagColor(null);
    }
  };

  // Remove a tag
  const removeTag = (tag) => {
    onTagsChange(selectedTags.filter(t => t.tag_id !== tag.tag_id));
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      setShowColorPicker(false);
      return;
    }
    
    if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      removeTag(selectedTags[selectedTags.length - 1]);
      return;
    }
    
    if (!showDropdown) return;
    
    const totalItems = suggestions.length + (inputValue.trim() ? 1 : 0); // +1 for "Create" option
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % totalItems);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + totalItems) % totalItems);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        selectTag(suggestions[highlightedIndex]);
      } else if (highlightedIndex === suggestions.length && inputValue.trim()) {
        // Create new tag option selected
        setShowColorPicker(true);
      } else if (inputValue.trim() && suggestions.length === 0) {
        setShowColorPicker(true);
      }
    }
  };

  const showCreateOption = inputValue.trim() && 
    !suggestions.some(t => t.name.toLowerCase() === inputValue.toLowerCase().trim());

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected Tags + Input */}
      <div 
        className={cn(
          "flex flex-wrap items-center gap-1.5 p-2 rounded-lg border min-h-[42px]",
          colors.bgInput,
          colors.borderPrimary,
          disabled && "opacity-50 cursor-not-allowed"
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {selectedTags.map(tag => (
          <TagBadge
            key={tag.tag_id}
            tag={tag}
            removable={!disabled}
            onRemove={removeTag}
            size="sm"
          />
        ))}
        
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
            setHighlightedIndex(-1);
          }}
          onFocus={() => {
            setShowDropdown(true);
            if (inputValue) fetchSuggestions(inputValue);
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={selectedTags.length === 0 ? placeholder : ""}
          className={cn(
            "flex-1 min-w-[120px] outline-none bg-transparent text-sm",
            colors.textPrimary
          )}
        />
        
        {isLoading && (
          <Loader2 className={cn("w-4 h-4 animate-spin", colors.textMuted)} />
        )}
      </div>
      
      {/* Dropdown */}
      {showDropdown && (suggestions.length > 0 || showCreateOption) && !showColorPicker && (
        <div className={cn(
          "absolute z-50 w-full mt-1 rounded-lg border shadow-lg overflow-hidden",
          colors.bgCard,
          colors.borderPrimary
        )}>
          <div className="max-h-[200px] overflow-y-auto py-1">
            {suggestions.map((tag, index) => (
              <button
                key={tag.tag_id}
                type="button"
                onClick={() => selectTag(tag)}
                className={cn(
                  "w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors",
                  index === highlightedIndex ? colors.bgTertiary : "hover:bg-gray-50 dark:hover:bg-gray-800",
                  colors.textPrimary
                )}
              >
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1">{tag.name}</span>
                {tag.usage_count > 0 && (
                  <span className={cn("text-xs", colors.textMuted)}>
                    {tag.usage_count} uses
                  </span>
                )}
              </button>
            ))}
            
            {showCreateOption && (
              <button
                type="button"
                onClick={() => setShowColorPicker(true)}
                className={cn(
                  "w-full px-3 py-2 flex items-center gap-2 text-left text-sm transition-colors border-t",
                  highlightedIndex === suggestions.length ? colors.bgTertiary : "hover:bg-gray-50 dark:hover:bg-gray-800",
                  colors.borderPrimary
                )}
              >
                <Plus className={cn("w-4 h-4", colors.textMuted)} />
                <span className={colors.textPrimary}>
                  Create &quot;<span className="font-medium">{inputValue.trim()}</span>&quot;
                </span>
              </button>
            )}
          </div>
        </div>
      )}
      
      {/* Color Picker for New Tag */}
      {showColorPicker && (
        <div className={cn(
          "absolute z-50 w-full mt-1 rounded-lg border shadow-lg p-3",
          colors.bgCard,
          colors.borderPrimary
        )}>
          <p className={cn("text-sm font-medium mb-2", colors.textPrimary)}>
            Pick a color for &quot;{inputValue.trim()}&quot;
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {TAG_COLORS.map(color => (
              <button
                key={color.value}
                type="button"
                onClick={() => setNewTagColor(color.value)}
                className={cn(
                  "w-7 h-7 rounded-full border-2 transition-transform hover:scale-110",
                  newTagColor === color.value ? "border-gray-600 scale-110" : "border-transparent"
                )}
                style={{ backgroundColor: color.value }}
                title={color.name}
              />
            ))}
            {/* Custom color picker */}
            <label className="relative w-7 h-7 rounded-full border border-dashed border-gray-400 hover:border-gray-600 cursor-pointer overflow-hidden">
              <input
                type="color"
                value={newTagColor || '#6366f1'}
                onChange={(e) => setNewTagColor(e.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                title="Custom Color"
              />
              <div 
                className="w-full h-full rounded-full"
                style={{ background: `conic-gradient(red, yellow, lime, aqua, blue, magenta, red)` }}
              />
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowColorPicker(false);
                setNewTagColor(null);
              }}
              className={cn("px-3 py-1.5 text-sm rounded-md", colors.textMuted, "hover:bg-gray-100 dark:hover:bg-gray-800")}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => createTag(inputValue.trim(), newTagColor)}
              disabled={isLoading}
              className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Create
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagInput;
